import React, { useContext } from "react";
import { AppContext } from "../context/AppContext";
import { Link } from "react-router-dom";
import { TrashIcon, PlusIcon, MinusIcon } from '@heroicons/react/solid';
import { formatCurrency } from '../utils/formatCurrency';

const placeholderImage = 'https://via.placeholder.com/150';

const CartPage = () => {
  const { cart, removeFromCart, updateCartItemQuantity, cartTotal } = useContext(AppContext);

  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity > 0) {
      updateCartItemQuantity(itemId, newQuantity);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Shopping Cart</h1>
      {cart.length === 0 ? (
        <div className="text-center">
          <p className="text-xl mb-4">Your cart is empty.</p>
          <Link to="/" className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition duration-300">
            Continue Shopping
          </Link>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:w-2/3">
            {cart.map((item) => (
              <div key={item.id} className="flex items-center border-b border-gray-200 py-4">
                <img 
                  src={Array.isArray(item.images) ? item.images[0] : (item.images || placeholderImage)} 
                  alt={item.name} 
                  className="w-24 h-24 object-contain rounded"
                  onError={(e) => { e.target.onerror = null; e.target.src = placeholderImage; }}
                />
                <div className="ml-4 flex-grow">
                  <h2 className="text-lg font-semibold">{item.name}</h2>
                  <p className="text-gray-600">{formatCurrency(parseFloat(item.price) || 0)}</p>
                  <div className="flex items-center mt-2">
                    <button 
                      onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <MinusIcon className="h-5 w-5" />
                    </button>
                    <span className="mx-2 w-8 text-center">{item.quantity}</span>
                    <button 
                      onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <PlusIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <p className="font-semibold text-lg w-24 text-right">
                  {formatCurrency((parseFloat(item.price) || 0) * item.quantity)}
                </p>
                <button 
                  onClick={() => removeFromCart(item.id)}
                  className="ml-6 text-red-500 hover:text-red-700"
                >
                  <TrashIcon className="h-6 w-6" />
                </button>
              </div>
            ))}
          </div>
          <div className="lg:w-1/3">
            <div className="bg-gray-50 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4">Order Summary</h2>
              <div className="flex justify-between mb-2">
                <span>Subtotal</span>
                <span>{formatCurrency(cartTotal)}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span>Shipping</span>
                <span>Calculated at checkout</span>
              </div>
              <div className="border-t border-gray-200 mt-4 pt-4">
                <div className="flex justify-between mb-2">
                  <span className="font-semibold">Total</span>
                  <span className="font-semibold">{formatCurrency(cartTotal)}</span>
                </div>
              </div>
              <div className="mt-8">
                <Link
                  to="/checkout"
                  className="bg-black text-white px-6 py-3 rounded-full font-semibold hover:bg-opacity-90 transition-colors"
                >
                  Proceed to Checkout
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;